import React, { useState, useEffect } from 'react';
import { useTenant } from '../../context/TenantContext';
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Modal,
  Grid,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert
} from '@mui/material';
import { FaTrash, FaUserPlus } from 'react-icons/fa';
import Swal from 'sweetalert2';

const UserManagement = ({ isSidebarOpen }) => {
  const { tenantID } = useTenant();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', isAdmin: false });
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (tenantID) {
      fetchUsers();
    }
  }, [tenantID]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_TENANT_API_URL || 'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod';
      const response = await fetch(`${apiUrl}/v2/users?tenantID=${tenantID}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!newUser.email || !newUser.email.includes('@')) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter a valid email address',
        icon: 'error',
        confirmButtonColor: '#d33'
      });
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_TENANT_API_URL || 'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod';
      const response = await fetch(`${apiUrl}/v2/users/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tenantID,
          email: newUser.email,
          isAdmin: newUser.isAdmin
        })
      });

      if (response.ok) {
        Swal.fire({
          title: 'User Invited!',
          text: `Invitation sent to ${newUser.email}`,
          icon: 'success',
          confirmButtonColor: '#342ead'
        });
        setNewUser({ email: '', isAdmin: false });
        setShowModal(false);
        fetchUsers(); // Refresh the list
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to invite user');
      }
    } catch (err) {
      Swal.fire({
        title: 'Error',
        text: err.message,
        icon: 'error',
        confirmButtonColor: '#d33'
      });
    }
  };

  const handleDeleteUser = async (email) => {
    const result = await Swal.fire({
      title: 'Confirm Delete',
      text: `Are you sure you want to remove ${email} from this tenant?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, remove',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#342ead'
    });

    if (result.isConfirmed) {
      try {
        const apiUrl = process.env.REACT_APP_TENANT_API_URL || 'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod';
        const response = await fetch(`${apiUrl}/v2/users?tenantID=${tenantID}&email=${email}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          Swal.fire({
            title: 'User Removed!',
            text: 'User has been removed from the tenant',
            icon: 'success',
            confirmButtonColor: '#342ead'
          });
          fetchUsers(); // Refresh the list
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete user');
        }
      } catch (err) {
        Swal.fire({
          title: 'Error',
          text: err.message,
          icon: 'error',
          confirmButtonColor: '#d33'
        });
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CONFIRMED': return 'success';
      case 'FORCE_CHANGE_PASSWORD': return 'warning';
      case 'UNCONFIRMED': return 'info';
      default: return 'default';
    }
  };

  const getRoleColor = (isAdmin) => {
    return isAdmin ? 'error' : 'primary';
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ padding: '20px', paddingTop: '100px' }}>
        <Typography>Loading users...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ borderRadius: "8px", padding: "20px", marginBottom: "30px", boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)" }}>
      {error && (
        <Alert severity="error" sx={{ marginBottom: '20px' }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          padding: '20px',
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Actions Bar */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}
        >
          <Button
            variant="contained"
            startIcon={<FaUserPlus />}
            onClick={() => setShowModal(true)}
            sx={{
              backgroundColor: '#342ead',
              textTransform: 'none'
            }}
          >
            Invite User
          </Button>
          
          <TextField
            label="Search Users"
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: '300px' }}
          />
        </Box>

        {/* Users Table */}
        <TableContainer component={Paper} sx={{ borderRadius: '8px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Admin</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user, index) => (
                  <TableRow key={index}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.isAdmin ? 'Admin' : 'User'}
                        color={getRoleColor(user.isAdmin)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.status}
                        color={getStatusColor(user.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.invitedAt ? new Date(user.invitedAt).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        startIcon={<FaTrash />}
                        onClick={() => handleDeleteUser(user.email)}
                        sx={{
                          borderColor: '#dc3545',
                          color: '#dc3545',
                          '&:hover': {
                            backgroundColor: '#f8d7da',
                            borderColor: '#dc3545'
                          }
                        }}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Invite User Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '500px',
            maxWidth: '90%',
            bgcolor: 'background.paper',
            borderRadius: '12px',
            boxShadow: 24,
            p: 4
          }}
        >
          <Typography variant="h5" gutterBottom>
            Invite New User
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Email Address"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Admin Privileges</InputLabel>
                <Select
                  value={newUser.isAdmin}
                  onChange={(e) => setNewUser({ ...newUser, isAdmin: e.target.value })}
                  label="Admin Privileges"
                >
                  <MenuItem value={false}>Regular User</MenuItem>
                  <MenuItem value={true}>Admin User</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '20px' }}>
            <Button
              variant="contained"
              onClick={handleInviteUser}
              sx={{
                backgroundColor: '#342ead',
                textTransform: 'none'
              }}
            >
              Send Invitation
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowModal(false)}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default UserManagement;